import type { DragEndEvent } from '@dnd-kit/core';
import { closestCenter, DndContext } from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import DragIndicator from '@mui/icons-material/DragIndicator';
import IconButton from '@mui/material/IconButton';
import TrailEnd from '@src/common/assets/trail-end.svg?react';
import TrailLine from '@src/common/assets/trail-line.svg?react';
import TrailMiddle from '@src/common/assets/trail-middle.svg?react';
import TrailStart from '@src/common/assets/trail-start.svg?react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
} from '@src/common/components/Table';
import { TableRow } from '@src/common/components/TableRow';
import { css } from '@src/common/styles/Styles';
import { theme } from '@src/common/styles/Theme';
import type { ReactNode } from 'react';
import { useEffect } from 'react';
import React from 'react';
import { useState } from 'react';

export type RowDefinition = {
  id: string;
  columnsContent: ReactNode[];
};

export type Row = {
  subRows?: RowDefinition[];
} & RowDefinition;

type Props = {
  columnsName: string[];
  rowsContent: Row[];
  onContentReordered?: (updatedRows: Row[]) => void;
  ariaLabelledBy?: string;
  disabled?: boolean;
  highlightedRow?: string;
};

export const DraggableTable = ({
  columnsName,
  rowsContent,
  onContentReordered,
  ariaLabelledBy,
  disabled,
  highlightedRow,
}: Props) => {
  const [rows, setRows] = useState(rowsContent);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      setRows((prevRows) => {
        const oldIndex = prevRows.findIndex((item) => item.id === active.id);
        const newIndex = prevRows.findIndex((item) => item.id === over?.id);

        const updatedRows = arrayMove(prevRows, oldIndex, newIndex);

        if (onContentReordered) {
          onContentReordered(updatedRows);
        }

        return updatedRows;
      });
    }
  };

  useEffect(() => {
    setRows(rowsContent);
  }, [rowsContent]);

  return (
    <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <Table
        aria-labelledby={ariaLabelledBy}
        style={{ overflow: 'hidden' }}
        size="small"
      >
        <TableHead>
          <TableRow key={`${ariaLabelledBy}-header-row`}>
            <TableCell
              key={`${ariaLabelledBy}-drag-and-drop-column`}
              style={{ width: 1 }}
            />
            <TableCell key={`${ariaLabelledBy}-tree`} style={{ width: 1 }} />
            {columnsName.map((column, i) => (
              <TableCell
                key={`${ariaLabelledBy}-column-${i}`}
                style={{ paddingLeft: 0 }}
              >
                {column}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          <SortableContext
            items={rows.map((row) => row.id)}
            strategy={verticalListSortingStrategy}
          >
            {rows.map((row) => (
              <DraggableRow
                key={row.id}
                id={row.id}
                columnsContent={row.columnsContent}
                disabled={disabled}
                subRows={row.subRows}
                highlightedRow={highlightedRow}
              />
            ))}
          </SortableContext>
        </TableBody>
      </Table>
    </DndContext>
  );
};

type RowProps = {
  id: string;
  columnsContent: React.ReactNode[];
  disabled?: boolean;
  highlightedRow?: string;
};

const SELECTED_ROW_STYLE = {
  backgroundColor: '#F1F2F6',
};

const DraggableRow = ({
  id,
  columnsContent,
  disabled,
  subRows,
  highlightedRow,
}: RowProps & { subRows?: RowProps[] }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled });

  const highlightRow = highlightedRow === id;

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
  };
  const hasSubRows = !!subRows && subRows.length > 0;

  return (
    <>
      <TableRow
        key={`row-${id}`}
        ref={setNodeRef}
        style={
          disabled
            ? undefined
            : { ...style, ...(highlightRow ? SELECTED_ROW_STYLE : {}) }
        }
        hover
      >
        <TableCell key={`row-${id}-drag-and-drop`} className={tableCell}>
          <IconButton
            {...attributes}
            {...listeners}
            aria-label="drag-and-drop"
            disabled={disabled}
          >
            <DragIndicator />
          </IconButton>
        </TableCell>
        <TableCell key={`row-${id}-tree`} className={tableCell}>
          {hasSubRows && <TrailStart className={trailStart} />}
        </TableCell>
        {columnsContent.map((column, i) => (
          <TableCell key={`row-${id}-column-${i}`} className={tableCell}>
            {column}
          </TableCell>
        ))}
      </TableRow>

      {subRows?.map((subRow, i) => (
        <SubRow
          style={disabled ? undefined : style}
          key={`row-${id}-sub-row-${subRow.id}`}
          {...subRow}
          parentBeingDragged={isDragging}
          setNodeRef={setNodeRef}
          lastRow={i === subRows?.length - 1}
          highlightRow={subRow.id === highlightedRow}
        />
      ))}
    </>
  );
};

type SubRowProps = {
  id: string;
  columnsContent: React.ReactNode[];
  parentBeingDragged: boolean;
  style?: object;
  setNodeRef: (node: HTMLElement | null) => void;
  lastRow: boolean;
  highlightRow?: boolean;
};

const SubRow = ({
  id,
  columnsContent,
  parentBeingDragged,
  style,
  lastRow,
  highlightRow,
}: SubRowProps) => {
  const backgroundStyle = parentBeingDragged
    ? { backgroundColor: theme.palette.table.rowDraggingColor }
    : {};
  const highlightStyle = highlightRow ? SELECTED_ROW_STYLE : {};

  return (
    <TableRow
      key={`sub-row-${id}`}
      style={{ ...style, ...highlightStyle, ...backgroundStyle }}
      hover
    >
      <TableCell key={`sub-row-${id}-empty`} className={tableCell} />
      <TableCell key={`sub-row-${id}-tree`} className={tableCell}>
        {lastRow ? (
          <TrailEnd className={trailEnd} />
        ) : (
          <>
            <TrailMiddle className={trailMiddle} />
            <TrailLine className={trailLine} />
          </>
        )}
      </TableCell>
      {columnsContent.map((column, i) => (
        <TableCell key={`sub-row-${id}-column-${i}`} className={tableCell}>
          {column}
        </TableCell>
      ))}
    </TableRow>
  );
};

const tableCell = css({
  padding: `2px 0`,
  border: 0,
});

const trailStart = css({
  position: 'relative',
  top: 16,
  left: 0,
});

const trailMiddle = css({
  position: 'relative',
  top: -8,
  left: 0,
});

const trailLine = css({
  position: 'relative',
  top: 6,
  left: -10,
});

const trailEnd = css({
  position: 'relative',
  top: -10,
  left: 0,
});
